import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  head: () => ({
    meta: [
      { title: "Admin — Configuración | Mine Class" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SettingsAdmin,
});

type Settings = {
  brochure_path: string;
  brochure_auto_send: boolean;
  crm_sender_email: string;
  crm_sender_name: string;
  google_sheets_leads_id: string;
  google_sheets_leads_tab: string;
};

const DEFAULTS: Settings = {
  brochure_path: "",
  brochure_auto_send: true,
  crm_sender_email: "ventas@mine-class.cl",
  crm_sender_name: "Mine Class",
  google_sheets_leads_id: "",
  google_sheets_leads_tab: "Leads",
};

function SettingsAdmin() {
  const [s, setS] = useState<Settings>(DEFAULTS);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    const { data } = await supabase.from("app_settings").select("key,value");
    const merged = { ...DEFAULTS };
    (data ?? []).forEach((r: { key: string; value: unknown }) => {
      if (r.key in merged) (merged as Record<string, unknown>)[r.key] = r.value;
    });
    setS(merged);
  }
  useEffect(() => { load(); }, []);

  async function saveOne<K extends keyof Settings>(key: K, value: Settings[K]) {
    await supabase.from("app_settings").upsert({ key, value: value as unknown as never }, { onConflict: "key" });
    setSavedMsg(`Guardado: ${key}`);
    setTimeout(() => setSavedMsg(null), 1500);
  }

  async function uploadBrochure(file: File) {
    setUploading(true);
    try {
      const path = `brochure-${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage.from("brochures").upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw error;
      setS((prev) => ({ ...prev, brochure_path: path }));
      await saveOne("brochure_path", path);
    } catch (e) {
      alert(`Error subiendo: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function previewBrochure() {
    if (!s.brochure_path) return;
    const { data } = await supabase.storage.from("brochures").createSignedUrl(s.brochure_path, 60 * 10);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  }

  return (
    <div className="max-w-3xl space-y-6">
      {savedMsg && (
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-3 py-2">{savedMsg}</div>
      )}

      <Section title="Brochure automático">
        <p className="text-sm text-[var(--color-on-surface-variant)]">
          Sube el PDF del brochure que se enviará automáticamente por correo a quienes completen el formulario de contacto.
        </p>
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept="application/pdf" disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBrochure(f); }}
            className="text-sm" />
          {uploading && <span className="text-xs">Subiendo…</span>}
        </div>
        <div className="text-xs text-[var(--color-on-surface-variant)]">
          Archivo actual: <span className="font-mono">{s.brochure_path || "(ninguno)"}</span>
          {s.brochure_path && (
            <button onClick={previewBrochure} className="ml-2 underline">Ver PDF</button>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.brochure_auto_send}
            onChange={(e) => { setS({ ...s, brochure_auto_send: e.target.checked }); saveOne("brochure_auto_send", e.target.checked); }} />
          Enviar brochure automáticamente al recibir un nuevo formulario
        </label>
      </Section>

      <Section title="Remitente">
        <Field label="Correo remitente"
          value={s.crm_sender_email}
          onChange={(v) => setS({ ...s, crm_sender_email: v })}
          onSave={(v) => saveOne("crm_sender_email", v)} />
        <Field label="Nombre remitente"
          value={s.crm_sender_name}
          onChange={(v) => setS({ ...s, crm_sender_name: v })}
          onSave={(v) => saveOne("crm_sender_name", v)} />
        <p className="text-xs text-[var(--color-on-surface-variant)]">
          El dominio del correo remitente debe estar verificado en <strong>Cloud → Emails</strong> para que los correos se envíen.
        </p>
      </Section>

      <Section title="Sincronización con Google Sheets">
        <p className="text-sm text-[var(--color-on-surface-variant)]">
          Cada nuevo lead se replicará automáticamente como fila en la hoja de cálculo que configures aquí. Requiere conectar el conector de Google Sheets desde Configuración → Conectores.
        </p>
        <Field label="ID de la hoja (Spreadsheet ID)"
          value={s.google_sheets_leads_id}
          onChange={(v) => setS({ ...s, google_sheets_leads_id: v })}
          onSave={(v) => saveOne("google_sheets_leads_id", v)}
          placeholder="1abcXYZ… (parte de la URL entre /d/ y /edit)" />
        <Field label="Nombre de la pestaña"
          value={s.google_sheets_leads_tab}
          onChange={(v) => setS({ ...s, google_sheets_leads_tab: v })}
          onSave={(v) => saveOne("google_sheets_leads_tab", v)} />
        <p className="text-xs text-[var(--color-on-surface-variant)]">
          Columnas esperadas (fila 1): fecha, nombre, email, teléfono, empresa, cargo, país, mensaje, fuente, utm_source, utm_medium, utm_campaign
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-[var(--color-outline-variant)] p-5 space-y-3">
      <h2 className="font-bold">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, onSave, placeholder }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onSave: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} onBlur={(e) => onSave(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1 border border-[var(--color-outline-variant)] px-3 py-2 text-sm" />
    </label>
  );
}
